import { useState } from "react";
import { useQuery } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

function parseUsage(usageJson: string | undefined) {
  try {
    const parsed = JSON.parse(usageJson ?? "{}") as {
      usage?: { input_tokens?: number; prompt_tokens?: number };
      timing?: { elapsed_seconds?: number; queue_seconds?: number };
    };
    return {
      input: parsed.usage?.input_tokens ?? parsed.usage?.prompt_tokens,
      apiSeconds: parsed.timing?.elapsed_seconds,
      queueSeconds: parsed.timing?.queue_seconds,
    };
  } catch {
    /* Unsettled or malformed receipts remain visible through their state. */
    return {};
  }
}

const finite = (value: number | undefined): value is number => typeof value === "number" && Number.isFinite(value);

/** Debug-mode only: model, spend and per-call timing for one AI run. */
export function AiCountAudit({ runId }: { runId: Id<"takeoffPilotAiRuns"> }) {
  const [open, setOpen] = useState(false);
  const audit = useQuery(api.takeoffPilot.jobs.getAiCountRunAudit, open ? { runId } : "skip");
  return (
    <details onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary className="cursor-pointer font-medium">API calls and timing</summary>
      {audit && (
        <p className="pt-1">
          {audit.run.model} · ${(audit.run.chargedMicros / 1e6).toFixed(4)} charged · ${(audit.run.reservedMicros / 1e6).toFixed(4)} reserved
        </p>
      )}
      {audit?.auditUrl && (
        <a className="underline" href={audit.auditUrl} target="_blank" rel="noreferrer">
          Download run audit
        </a>
      )}
      <div className="max-h-56 space-y-2 overflow-auto pt-2">
        {audit?.calls.map((call) => {
          const { input, apiSeconds, queueSeconds } = parseUsage(call.usageJson);
          return (
            <div className="border-t pt-1" key={call.stage}>
              <p className="font-medium">{call.stage} · {call.state}</p>
              <p>
                {call.chargedMicros !== undefined
                  ? `$${(call.chargedMicros / 1e6).toFixed(6)} charged`
                  : `$${(call.reservedMicros / 1e6).toFixed(6)} reserved`}
                {finite(input) ? ` · ${input.toLocaleString()} input tokens` : ""}
              </p>
              <p>
                {finite(apiSeconds) ? `${apiSeconds.toFixed(1)}s API` : ""}
                {finite(queueSeconds) ? ` · ${queueSeconds.toFixed(1)}s waiting` : ""}
              </p>
            </div>
          );
        })}
      </div>
    </details>
  );
}
